import { MediaCarousel } from '@euler/app/components/media/MediaCarousel';
import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { keyOfIssue } from '@euler/app/flows/task/pages/quotation/functions/utils';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import {
  getDefectiveLevelBgColor,
  getDefectiveLevelColor,
  getDefectiveLevelFrom,
  getDefectiveLevelText,
} from '@euler/functions/getDefectiveLevel';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject } from '@euler/model';
import { QuotationIssuePayload } from '@euler/model/task-detail';
import { memo, useMemo } from 'react';
import { Platform, StyleProp, StyleSheet, View, ViewStyle } from 'react-native';
import { useTheme } from 'styled-components';

export const SubjectIssueList = memo(
  ({
    issues,
    style,
  }: {
    issues: QuotationIssuePayload[];
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();

    const medias = useMemo(
      () =>
        issues
          .flatMap(x => x.medias)
          .map<MediaObject>(x => ({
            id: x.cid,
            type: x.type.includes('image') ? 'image' : 'video',
            url: x.url,
            coverUrl: x.coverUrl,
          })),
      [issues],
    );

    if (!issues.length) {
      return null;
    }

    return (
      <StackLayout style={style} gap={rpx(2)}>
        <StackLayout
          gap={0}
          divider={
            <View
              style={{
                borderTopWidth: StyleSheet.hairlineWidth,
                borderStyle: Platform.OS === 'web' ? 'dashed' : 'solid',
                borderColor: StatusColors.Warn,
                marginVertical: rpx(5),
              }}
            />
          }
        >
          {issues.map(issue => (
            <IssueListItem key={keyOfIssue(issue)} issue={issue} />
          ))}
          <View style={{ height: 0 }} />
        </StackLayout>
        {medias.length > 0 && (
          <MediaCarousel
            medias={medias}
            display="list"
            size={rpx(64)}
            playIconSize={rpx(21)}
            itemSpacer={rpx(5)}
            itemStyle={{ borderRadius: rpx(5), overflow: 'hidden' }}
          />
        )}
      </StackLayout>
    );
  },
);

export const IssueListItem = memo(
  ({
    issue,
    style,
  }: {
    issue: QuotationIssuePayload;
    style?: StyleProp<ViewStyle>;
  }) => {
    const theme = useTheme();
    const { rpx } = useResponsiveContext();

    const title = issue.isCustomIssue ? issue.siteName : issue.itemName;
    const description = issue.isCustomIssue
      ? issue.inspectionResult
      : issue.title;

    const defectiveLevel = getDefectiveLevelFrom(issue);

    return (
      <StackLayout direction="row" style={style}>
        <HStack flex={1} justifyContent="flex-start">
          <Typography.Text size={rpx(12)}>{`${title}: `}</Typography.Text>
          <Typography.Text size={rpx(12)} color="#777">
            {description ?? ''}
          </Typography.Text>
        </HStack>
        <Center
          css={`
            padding: 0 ${rpx(7)}px;
            border-radius: ${rpx(44)}px;
            background-color: ${getDefectiveLevelBgColor(
              theme,
              defectiveLevel,
            )};
          `}
        >
          <Typography.Text
            size={rpx(10)}
            color={getDefectiveLevelColor(theme, defectiveLevel)}
          >
            {getDefectiveLevelText(defectiveLevel)}
          </Typography.Text>
        </Center>
      </StackLayout>
    );
  },
);
